/**
 * Widget A: Retirement Readiness Overview
 * 
 * Displays a donut chart showing the distribution of clients across
 * retirement readiness categories: Fully funded (≥90%), Minor gap (70-89.9%),
 * and Significant gap (<70%).
 */

import React, { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateRetirementReadiness, RetirementReadinessData } from "../utils/widgetCalculations";
import { UNIFIED_CHART_COLORS, SEMANTIC_PASTELS, ACCENT_COLOR } from "./widgetColors";
import { ChartTooltip, CHART_MARGINS, AXIS_STYLE, CHART_ANIMATION, renderPieLabel } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";

interface RetirementReadinessWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

// Use unified colors with semantic meaning
const COLORS = {
  fullyFunded: SEMANTIC_PASTELS.success,      // Mint green (unified pastel)
  minorGap: SEMANTIC_PASTELS.warning,         // Lemon yellow (unified pastel)
  significantGap: SEMANTIC_PASTELS.error,     // Coral (unified pastel)
};

const chartConfig = {
  fullyFunded: {
    label: "Fully Funded (≥90%)",
    color: COLORS.fullyFunded,
  },
  minorGap: {
    label: "Minor Gap (70-89.9%)",
    color: COLORS.minorGap,
  },
  significantGap: {
    label: "Significant Gap (<70%)",
    color: COLORS.significantGap,
  },
};

export function RetirementReadinessWidget({
  clients,
  isLoading = false,
  userId,
}: RetirementReadinessWidgetProps) {
  const api = XanoAPI.getInstance();

  const { data: apiData, isLoading: apiLoading, error: apiError } = useQuery({
    queryKey: ["retirement-readiness", userId],
    queryFn: async () => {
      const result = await api.getRetirementReadiness(userId);
      console.log("🔍 RetirementReadinessWidget: API response:", result);
      console.log("🔍 RetirementReadinessWidget: API response type:", typeof result);
      console.log("🔍 RetirementReadinessWidget: Is null?", result === null);
      console.log("🔍 RetirementReadinessWidget: Has buckets?", !!result?.buckets);
      console.log("🔍 RetirementReadinessWidget: Buckets value:", result?.buckets);
      console.log("🔍 RetirementReadinessWidget: Total clients:", result?.totalClients);
      if (result) {
        console.log("🔍 RetirementReadinessWidget: Full result structure:", JSON.stringify(result, null, 2));
      }
      return result;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Log any errors
  useEffect(() => {
    if (apiError) {
      console.error("❌ RetirementReadinessWidget: Query error:", apiError);
    }
  }, [apiError]);

  const data = useMemo(() => {
    let result: RetirementReadinessData;
    
    console.log("🔍 RetirementReadinessWidget: Processing data - apiData:", apiData);
    console.log("🔍 RetirementReadinessWidget: apiData is null?", apiData === null);
    console.log("🔍 RetirementReadinessWidget: apiData is undefined?", apiData === undefined);
    console.log("🔍 RetirementReadinessWidget: Clients available:", clients.length);
    
    if (apiData && apiData.buckets && apiData.totalClients > 0) {
      // Use API data if available and has clients
      console.log("✅ RetirementReadinessWidget: Using API data");
      console.log("🔍 RetirementReadinessWidget: Buckets structure:", JSON.stringify(apiData.buckets, null, 2));
      console.log("🔍 RetirementReadinessWidget: Total clients from API:", apiData.totalClients);
      result = apiData as RetirementReadinessData;
    } else if (clients.length > 0) {
      // API data is empty or unavailable - try calculating from clients
      console.log("⚠️ RetirementReadinessWidget: API data empty or unavailable, trying client calculation");
      try {
        const calculatedData = calculateRetirementReadiness(clients);
        console.log("🔍 RetirementReadinessWidget: Calculated from clients - Total:", calculatedData.totalClients);
        console.log("🔍 RetirementReadinessWidget: Calculated buckets:", JSON.stringify(calculatedData.buckets, null, 2));
        
        if (calculatedData.totalClients > 0) {
          console.log("✅ RetirementReadinessWidget: Using calculated data from clients");
          result = calculatedData;
        } else {
          console.log("⚠️ RetirementReadinessWidget: Calculated data also empty, returning empty structure");
          result = {
            buckets: {
              fullyFunded: { count: 0, percentage: 0, avgRatio: 0 },
              minorGap: { count: 0, percentage: 0, avgRatio: 0 },
              significantGap: { count: 0, percentage: 0, avgRatio: 0 },
            },
            totalClients: 0,
          };
        }
      } catch (error) {
        console.error("❌ RetirementReadinessWidget: Error calculating from clients:", error);
        result = {
          buckets: {
            fullyFunded: { count: 0, percentage: 0, avgRatio: 0 },
            minorGap: { count: 0, percentage: 0, avgRatio: 0 },
            significantGap: { count: 0, percentage: 0, avgRatio: 0 },
          },
          totalClients: 0,
        };
      }
    } else {
      // No API data and no clients - return empty structure
      console.log("❌ RetirementReadinessWidget: No API data and no clients, returning empty structure");
      console.log("🔍 RetirementReadinessWidget: apiData value:", apiData);
      console.log("🔍 RetirementReadinessWidget: apiData.buckets:", apiData?.buckets);
      result = {
        buckets: {
          fullyFunded: { count: 0, percentage: 0, avgRatio: 0 },
          minorGap: { count: 0, percentage: 0, avgRatio: 0 },
          significantGap: { count: 0, percentage: 0, avgRatio: 0 },
        },
        totalClients: 0,
      };
    }
    
    // Ensure result has buckets structure with all required properties
    if (!result || !result.buckets || 
        !result.buckets.fullyFunded || !result.buckets.minorGap || !result.buckets.significantGap) {
      // If structure is invalid, return empty structure
      console.log("⚠️ RetirementReadinessWidget: Invalid structure, returning empty");
      result = {
        buckets: {
          fullyFunded: { count: 0, percentage: 0, avgRatio: 0 },
          minorGap: { count: 0, percentage: 0, avgRatio: 0 },
          significantGap: { count: 0, percentage: 0, avgRatio: 0 },
        },
        totalClients: 0,
      };
    }
    
    // Safely access bucket properties with fallbacks
    const buckets = result.buckets || {
      fullyFunded: { count: 0, percentage: 0, avgRatio: 0 },
      minorGap: { count: 0, percentage: 0, avgRatio: 0 },
      significantGap: { count: 0, percentage: 0, avgRatio: 0 },
    };
    
    // Transform to chart data format
    const chartData = [
      {
        name: "Fully Funded",
        value: buckets.fullyFunded?.count || 0,
        percentage: buckets.fullyFunded?.percentage || 0,
        avgRatio: buckets.fullyFunded?.avgRatio || 0,
        fill: COLORS.fullyFunded,
        key: "fullyFunded",
      },
      {
        name: "Minor Gap",
        value: buckets.minorGap?.count || 0,
        percentage: buckets.minorGap?.percentage || 0,
        avgRatio: buckets.minorGap?.avgRatio || 0,
        fill: COLORS.minorGap,
        key: "minorGap",
      },
      {
        name: "Significant Gap",
        value: buckets.significantGap?.count || 0,
        percentage: buckets.significantGap?.percentage || 0,
        avgRatio: buckets.significantGap?.avgRatio || 0,
        fill: COLORS.significantGap,
        key: "significantGap",
      },
    ].filter((item) => item.value > 0); // Only show categories with clients

    return { 
      chartData, 
      result
    };
  }, [apiData, clients]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if we have any clients at all
  if (clients.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">
              No clients available to calculate retirement readiness.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if we have calculable data
  if (data.result.totalClients === 0 || data.chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">
              No clients with calculable retirement readiness data. Clients may be missing required information (age, retirement need, or projected income).
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardContent>
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={CHART_MARGINS}>
              <Pie
                data={data.chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => {
                  const percentage = entry.percentage !== undefined 
                    ? entry.percentage 
                    : (entry.payload?.percentage !== undefined ? entry.payload.percentage : 0);
                  if (percentage < 5) return "";
                  return (
                    <text
                      x={entry.x}
                      y={entry.y}
                      fill="hsl(var(--foreground))"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="13"
                      fontWeight="600"
                      style={{ 
                        textShadow: "0 1px 2px rgba(0,0,0,0.1), 0 0 4px rgba(255,255,255,0.8)",
                        filter: "drop-shadow(0 0 2px rgba(255,255,255,0.9))"
                      }}
                    >
                      {percentage.toFixed(0)}%
                    </text>
                  );
                }}
                outerRadius={95}
                innerRadius={60}
                paddingAngle={3}
                dataKey="value"
                {...CHART_ANIMATION}
              >
                {data.chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.fill}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                    opacity={0.85}
                  />
                ))}
              </Pie>
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value, name) => {
                    const dataPoint = data.chartData.find(d => d.value === value);
                    return [
                      `${value} clients (${dataPoint?.percentage.toFixed(1)}%)`,
                      dataPoint?.name || name
                    ];
                  }}
                />} 
                cursor={{ fill: "transparent" }}
              />
              <Legend
                verticalAlign="bottom"
                height={60}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ paddingTop: "15px" }}
                formatter={(value, entry: any) => {
                  const dataPoint = data.chartData.find((d) => d.key === entry.payload?.key);
                  if (!dataPoint) return value;
                  return (
                    <span className="text-xs text-muted-foreground">
                      {value} <span className="font-semibold text-foreground">{dataPoint.value}</span> ({dataPoint.percentage.toFixed(1)}%)
                    </span>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 pt-3 border-t">
          {data.chartData.map((item) => (
            <div key={item.key} className="space-y-1 text-center">
              <div 
                className="text-2xl font-bold" 
                style={{ 
                  color: item.fill,
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)"
                }}
              >
                {item.value}
              </div>
              <div className="text-xs font-medium text-foreground">{item.name}</div>
              <div className="text-xs text-muted-foreground">
                Avg: {(item.avgRatio * 100).toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

